import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { Lightbulb, Plus, ThumbsUp, ChevronDown, ChevronUp, CheckCircle, Clock, XCircle, Zap, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const CATEGORIES = [
  { value: "amelioration", label: "Amélioration", color: "bg-blue-100 text-blue-700" },
  { value: "idee", label: "Idée", color: "bg-purple-100 text-purple-700" },
  { value: "bug", label: "Problème / Bug", color: "bg-red-100 text-red-700" },
  { value: "remarque", label: "Remarque", color: "bg-amber-100 text-amber-700" },
  { value: "autre", label: "Autre", color: "bg-gray-100 text-gray-700" },
];

const STATUTS = {
  en_attente: { label: "En attente", icon: Clock, color: "bg-gray-100 text-gray-600" },
  en_cours: { label: "En cours", icon: Zap, color: "bg-blue-100 text-blue-700" },
  acceptee: { label: "Acceptée", icon: CheckCircle, color: "bg-green-100 text-green-700" },
  refusee: { label: "Refusée", icon: XCircle, color: "bg-red-100 text-red-600" },
  implemente: { label: "Implémenté", icon: CheckCircle, color: "bg-emerald-100 text-emerald-700" },
  classe: { label: "Classé", icon: CheckCircle, color: "bg-slate-100 text-slate-600" },
};

function StatutBadge({ statut }) {
  const s = STATUTS[statut] || STATUTS.en_attente;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  );
}

function CategorieBadge({ categorie }) {
  const c = CATEGORIES.find(x => x.value === categorie) || CATEGORIES[4];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
}

export function Propositions() {
  const { user } = useAuth();
  const { canDo } = usePermissions() || {};
  const [propositions, setPropositions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Formulaire
  const [form, setForm] = useState({ titre: "", description: "", categorie: "amelioration", anonyme: false, visibilite: "publique" });

  // Réponse admin
  const [adminReponse, setAdminReponse] = useState({});
  const [savingAdmin, setSavingAdmin] = useState(null);

  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `propositions_admin`.
  //   Contrôle : bloc réponse admin (statut + admin_reponse + supprimer).
  //   Backend (Vague 3a) fait déjà autorité via JWT — fail-secure ici.
  const isAdmin = !!(canDo && canDo('propositions_admin'));
  const myId = user?.id || "";
  const myNom = user ? `${user.prenom} ${user.nom}` : "";

  const loadPropositions = async () => {
    setLoading(true);
    try {
      // 🚨 [Vague 3a — 2026-08-25] `role` et `user_id` retirés des query
      // params — le backend lit l'identité depuis le JWT désormais.
      const r = await axios.get(`${API_URL}/api/propositions`);
      if (r.status === 200) {
        const d = r.data;
        setPropositions(d.propositions || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPropositions(); }, [myId]);

  const handleSubmit = async () => {
    if (!form.titre.trim() || !form.description.trim()) {
      toast.error("Titre et description requis");
      return;
    }
    setSubmitting(true);
    try {
      // 🚨 [Vague 3a — 2026-08-25] `auteur_id`/`auteur_nom` ne sont plus
      // envoyés — le backend les lit depuis le JWT (anti-usurpation).
      const r = await axios.post(`${API_URL}/api/propositions`, {
          titre: form.titre,
          description: form.description,
          categorie: form.categorie,
          anonyme: form.anonyme,
          visibilite: form.visibilite,
        });
      if (r.status === 200) {
        toast.success("Proposition soumise !");
        setForm({ titre: "", description: "", categorie: "amelioration", anonyme: false, visibilite: "publique" });
        setShowForm(false);
        loadPropositions();
      }
    } catch { toast.error("Erreur"); }
    finally { setSubmitting(false); }
  };

  const handleVote = async (propId) => {
    // 🚨 [Vague 3a — 2026-08-25] `user_id` retiré du body — le backend lit
    // l'identité du votant depuis le JWT.
    await axios.post(`${API_URL}/api/propositions/${propId}/vote`, {});
    loadPropositions();
  };

  const saveAdminReponse = async (prop) => {
    setSavingAdmin(prop.id);
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      await axios.put(`${API_URL}/api/propositions/${prop.id}/statut`, {
          statut: adminReponse[prop.id]?.statut || prop.statut,
          reponse_admin: adminReponse[prop.id]?.reponse || "",
        });
      toast.success("Réponse sauvegardée");
      loadPropositions();
    } catch { toast.error("Erreur"); }
    finally { setSavingAdmin(null); }
  };

  const handleDelete = async (propId, titre) => {
    if (!window.confirm(`Supprimer la proposition "${titre}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/propositions/${propId}`);
      toast.success("Proposition supprimée");
      loadPropositions();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
      console.error(e);
    }
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return "Hier";
    return `Il y a ${days} jours`;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Lightbulb className="w-7 h-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Propositions</h1>
            <p className="text-sm text-slate-500">Améliorons la plateforme ensemble</p>
          </div>
        </div>
        <Button
          data-testid="btn-nouvelle-proposition"
          onClick={() => setShowForm(!showForm)}
          className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
        >
          <Plus className="w-4 h-4" />
          Nouvelle proposition
        </Button>
      </div>

      {/* Formulaire */}
      {showForm && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="text-lg">Soumettre une proposition</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Titre <span className="text-red-500">*</span></Label>
              <Input
                data-testid="prop-titre"
                placeholder="Ex: Ajouter un export Excel pour les rapports..."
                value={form.titre}
                onChange={e => setForm({ ...form, titre: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description <span className="text-red-500">*</span></Label>
              <Textarea
                data-testid="prop-description"
                placeholder="Décrivez votre proposition en détail. Plus c'est précis, mieux c'est!"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={4}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <Select value={form.categorie} onValueChange={v => setForm({ ...form, categorie: v })}>
                  <SelectTrigger className="mt-1" data-testid="prop-categorie">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 mt-6">
                <Checkbox
                  id="anonyme"
                  data-testid="prop-anonyme"
                  checked={form.anonyme}
                  onCheckedChange={v => setForm({ ...form, anonyme: v })}
                />
                <Label htmlFor="anonyme" className="cursor-pointer">
                  Soumettre anonymement
                </Label>
              </div>
              {/* Visibilité : Publique / Privée */}
              <div className="flex items-center gap-4 mt-3">
                <span className="text-sm font-medium text-gray-700">Visibilité :</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, visibilite: "publique" })}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${form.visibilite === 'publique' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'}`}
                >
                  Publique
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, visibilite: "privee" })}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${form.visibilite === 'privee' ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                >
                  Privée (admin seulement)
                </button>
              </div>
            </div>
            {form.anonyme && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Votre identité sera masquée. Seul l'admin sait qui vous êtes.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button
                data-testid="btn-soumettre-proposition"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {submitting ? "Envoi..." : "Soumettre"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      {propositions.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {Object.entries(STATUTS).map(([key, val]) => {
            const count = propositions.filter(p => p.statut === key).length;
            if (!count) return null;
            const Icon = val.icon;
            return (
              <div key={key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${val.color}`}>
                <Icon className="w-3.5 h-3.5" />
                {val.label}: {count}
              </div>
            );
          })}
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <p className="text-gray-400 text-center py-10">Chargement...</p>
      ) : propositions.length === 0 ? (
        <Card>
          <CardContent className="text-center py-14 text-gray-400">
            <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-200" />
            <p className="font-medium">Aucune proposition pour l'instant</p>
            <p className="text-sm mt-1">Soyez le premier à proposer une amélioration!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {propositions.map(prop => {
            const isExpanded = expandedId === prop.id;
            const isMine = prop.auteur_id === myId;
            return (
              <Card key={prop.id} className={`transition-all ${isExpanded ? "shadow-md" : ""}`}>
                <CardContent className="p-0">
                  <button
                    className="w-full text-left p-4"
                    onClick={() => setExpandedId(isExpanded ? null : prop.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-800 text-sm">{prop.titre}</span>
                          <CategorieBadge categorie={prop.categorie} />
                          <StatutBadge statut={prop.statut} />
                          {isMine && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600 font-medium">Ma proposition</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <div className="flex items-center gap-1.5">
                            {!(prop.anonyme && !isMine) && prop.auteur_photo_url && (
                              <EmployeeAvatar photoUrl={prop.auteur_photo_url} prenom={prop.auteur_nom?.split(' ')[0] || ''} nom={prop.auteur_nom?.split(' ').slice(1).join(' ') || ''} size="xs" />
                            )}
                            <span>{prop.anonyme && !isMine ? "Anonyme" : prop.auteur_nom}</span>
                          </div>
                          <span>·</span>
                          <span>{timeAgo(prop.created_at)}</span>
                          <span>·</span>
                          <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {prop.votes || 0}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{prop.description}</p>

                      {/* Réponse admin existante */}
                      {prop.reponse_admin && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-blue-700 flex items-center gap-1 mb-1">
                            <MessageCircle className="w-3.5 h-3.5" /> Réponse de l'équipe
                          </p>
                          <p className="text-sm text-blue-800">{prop.reponse_admin}</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleVote(prop.id)}
                          className="gap-1.5 text-xs"
                        >
                          <ThumbsUp className="w-3.5 h-3.5" />
                          Voter ({prop.votes || 0})
                        </Button>
                      </div>

                      {/* Zone admin */}
                      {isAdmin && (
                        <div className="border-t border-gray-100 pt-3 space-y-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Gestion admin</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Statut</Label>
                              <Select
                                value={adminReponse[prop.id]?.statut || prop.statut}
                                onValueChange={v => setAdminReponse(prev => ({ ...prev, [prop.id]: { ...(prev[prop.id] || {}), statut: v } }))}
                              >
                                <SelectTrigger className="h-8 text-xs mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(STATUTS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <Textarea
                            placeholder="Réponse / commentaire pour l'employé..."
                            value={adminReponse[prop.id]?.reponse || prop.reponse_admin || ""}
                            onChange={e => setAdminReponse(prev => ({ ...prev, [prop.id]: { ...(prev[prop.id] || {}), reponse: e.target.value } }))}
                            rows={2}
                            className="text-sm"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => saveAdminReponse(prop)}
                              disabled={savingAdmin === prop.id}
                              className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                            >
                              {savingAdmin === prop.id ? "Sauvegarde..." : "Sauvegarder"}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(prop.id, prop.titre)}
                              className="gap-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      )}
                      {/* Bouton supprimer pour l'auteur (non-admin) */}
                      {!isAdmin && isMine && (
                        <div className="border-t border-gray-100 pt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(prop.id, prop.titre)}
                            className="gap-1 text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Supprimer ma proposition
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
